import { useState } from 'react'

// Presets are page-owned filter config. They are deliberately NEVER written
// to the ?filter= URL param (that stays user-filters-only): edits to editable
// presets reset to the developer defaults on reload, and shared links carry
// only the user's own filters -- the recipient's page re-applies its own
// presets. See the batch-1 design spec.
// Normalizes a page's declarative preset config (stable ids + filled defaults).
// Split out of usePresetFilters so pages whose presets change at runtime -- the
// incidents listing rebuilds them per tab -- can own the state themselves and
// still get the same shape.
export function buildPresets(presetConfig) {
    return (presetConfig || []).map((preset, index) => ({
        id: `preset-${index}`,
        fieldName: preset.fieldName,
        filterType: preset.filterType,
        filterValues: preset.filterValues !== undefined ? preset.filterValues : null,
        editable: !!preset.editable,
        visible: preset.visible !== false,
        // `choices` marks a CONSTRAINED preset: the page fixes its field and
        // operator and offers a closed set of values, so only the value is
        // editable, and the row can be neither removed nor left empty. Values
        // are strings, matching how enum options are advertised.
        //
        // The key is omitted rather than defaulted to null so that every
        // unconstrained preset keeps the exact object shape pages and tests
        // already expect.
        ...(preset.choices ? { choices: preset.choices } : {}),
    }))
}

export function usePresetFilters(presetConfig) {
    const [defaultPresets] = useState(() => buildPresets(presetConfig))
    const [presets, setPresets] = useState(defaultPresets)
    return [presets, setPresets, defaultPresets]
}

// The single FilterInput[] a page sends as the GraphQL `filters` variable.
export function combineFilters(presets, urlFilters) {
    return [
        ...(presets || []).map(({ fieldName, filterType, filterValues }) => ({ fieldName, filterType, filterValues })),
        ...(urlFilters || []),
    ]
}

// Header badges count what the user can SEE: visible presets + user filters.
export function countVisibleFilters(presets, urlFilters) {
    return (presets || []).filter((p) => p.visible !== false).length + (urlFilters || []).length
}
